import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { canWriteEmpresaData } from '../../auth/session';
import { formatCurrencyBR, formatDecimalBR, formatIntegerBR, normalizeCurrencyInputBR, normalizeDecimalInputBR, normalizeIntegerInputBR, parseCurrencyBR, parseDecimalBR, parseIntegerBR } from '../../utils/numberBR';

const DEFAULTS = {
  caixa_minimo: 50000,
  margem_alvo_pct: 20,
  prazo_retorno_alvo_dias: 365,
  ruptura_critica_dias: 20,
  estoque_estimado_pct: 12,
};

const toFormData = (config) => ({
  caixa_minimo: formatCurrencyBR(config.caixa_minimo),
  margem_alvo_pct: formatDecimalBR(config.margem_alvo_pct, 2),
  prazo_retorno_alvo_dias: formatIntegerBR(config.prazo_retorno_alvo_dias),
  ruptura_critica_dias: formatIntegerBR(config.ruptura_critica_dias),
  estoque_estimado_pct: formatDecimalBR(config.estoque_estimado_pct, 2),
});

const ConstrutorConfiguracoesPage = () => {
  const canWrite = canWriteEmpresaData();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(toFormData(DEFAULTS));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/construtor/configuracoes');
      setForm(toFormData({
        caixa_minimo: Number(data?.config?.caixa_minimo ?? DEFAULTS.caixa_minimo),
        margem_alvo_pct: Number(data?.config?.margem_alvo_pct ?? DEFAULTS.margem_alvo_pct),
        prazo_retorno_alvo_dias: Number(data?.config?.prazo_retorno_alvo_dias ?? DEFAULTS.prazo_retorno_alvo_dias),
        ruptura_critica_dias: Number(data?.config?.ruptura_critica_dias ?? DEFAULTS.ruptura_critica_dias),
        estoque_estimado_pct: Number(data?.config?.estoque_estimado_pct ?? DEFAULTS.estoque_estimado_pct),
      }));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar configuracoes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        caixa_minimo: parseCurrencyBR(form.caixa_minimo) ?? 0,
        margem_alvo_pct: parseDecimalBR(form.margem_alvo_pct) ?? 0,
        prazo_retorno_alvo_dias: parseIntegerBR(form.prazo_retorno_alvo_dias) ?? 0,
        ruptura_critica_dias: parseIntegerBR(form.ruptura_critica_dias) ?? 0,
        estoque_estimado_pct: parseDecimalBR(form.estoque_estimado_pct) ?? 0,
      };
      await axios.put('/api/construtor/configuracoes', payload);
      toast.success('Configuracoes salvas');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar configuracoes');
    } finally {
      setSaving(false);
    }
  };

  const setValue = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Configuracao Operacional</h1>
        <p className="text-gray-600">Parametros usados pelos alertas, score de risco e motor de decisao do modulo construtor.</p>
      </div>

      <form className="card" onSubmit={onSave}>
        <div className="card-header">Parametros de Operacao</div>
        <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Caixa minimo (R$)</label>
            <input
              className="form-input"
              type="text"
              inputMode="decimal"
              value={form.caixa_minimo}
              onChange={(e) => setValue('caixa_minimo', normalizeCurrencyInputBR(e.target.value))}
              disabled={!canWrite || loading || saving}
            />
            <p className="text-xs text-gray-500 mt-1">Dispara alerta quando o caixa disponivel fica abaixo deste valor.</p>
          </div>

          <div>
            <label className="form-label">Margem alvo (%)</label>
            <input
              className="form-input"
              type="text"
              inputMode="decimal"
              value={form.margem_alvo_pct}
              onChange={(e) => setValue('margem_alvo_pct', normalizeDecimalInputBR(e.target.value, 2))}
              disabled={!canWrite || loading || saving}
            />
            <p className="text-xs text-gray-500 mt-1">Usada para monitorar margem abaixo do esperado e precificacao minima.</p>
          </div>

          <div>
            <label className="form-label">Prazo alvo de retorno (dias)</label>
            <input
              className="form-input"
              type="text"
              inputMode="numeric"
              value={form.prazo_retorno_alvo_dias}
              onChange={(e) => setValue('prazo_retorno_alvo_dias', normalizeIntegerInputBR(e.target.value))}
              disabled={!canWrite || loading || saving}
            />
            <p className="text-xs text-gray-500 mt-1">Referencia para o score de saude e recomendacoes de inicio de novas obras.</p>
          </div>

          <div>
            <label className="form-label">Ruptura critica (dias sem movimento)</label>
            <input
              className="form-input"
              type="text"
              inputMode="numeric"
              value={form.ruptura_critica_dias}
              onChange={(e) => setValue('ruptura_critica_dias', normalizeIntegerInputBR(e.target.value))}
              disabled={!canWrite || loading || saving}
            />
            <p className="text-xs text-gray-500 mt-1">Define quando uma obra gera alerta de ruptura por inatividade.</p>
          </div>

          <div>
            <label className="form-label">Estoque estimado sobre custos (%)</label>
            <input
              className="form-input"
              type="text"
              inputMode="decimal"
              value={form.estoque_estimado_pct}
              onChange={(e) => setValue('estoque_estimado_pct', normalizeDecimalInputBR(e.target.value, 2))}
              disabled={!canWrite || loading || saving}
            />
            <p className="text-xs text-gray-500 mt-1">Estimativa para compor capital imobilizado quando nao houver estoque detalhado.</p>
          </div>
        </div>

        <div className="card-body border-t flex items-center gap-3">
          {canWrite && (
            <button className="btn-primary" type="submit" disabled={loading || saving}>
              {saving ? 'Salvando...' : 'Salvar Configuracoes'}
            </button>
          )}
          <button
            className="btn-secondary"
            type="button"
            onClick={load}
            disabled={loading || saving}
          >
            Recarregar
          </button>
          {!canWrite && <span className="text-sm text-gray-500">Perfil somente leitura: alteracoes desabilitadas.</span>}
        </div>
      </form>
    </div>
  );
};

export default ConstrutorConfiguracoesPage;
